import React, { useState, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useResponsive } from '../../hooks/useResponsive';
import { useModal } from '../../hooks/useModal';
import { Role } from '../../types';
import { iconButtonStyles, overlayStyles, cn } from '../../utils/classNames';
import Button from '../ui/Button';
import {
  UserIcon,
  ArrowRightOnRectangleIcon,
  BuildingOffice2Icon,
  Bars3Icon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import HelpButton from '../help/HelpButton';
import HelpModal from '../help/HelpModal';
import { HeaderEnvironmentBadge } from '../common/EnvironmentBadge';
import { getRoleLabel } from '../../utils/roleUtils';

interface HeaderProps {
  onMenuClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const { isMobile, isTablet } = useResponsive();
  const navigate = useNavigate();
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownButtonRef = useRef<HTMLButtonElement>(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
    setIsDropdownOpen(false);
  };

  const handleProfileClick = () => {
    navigate('/profile');
    setIsDropdownOpen(false);
  };

  // ドロップダウンモーダル管理
  const { handleOverlayClick } = useModal({
    isOpen: isDropdownOpen,
    onClose: () => setIsDropdownOpen(false),
    containerRef: dropdownRef,
    triggerRef: dropdownButtonRef,
    closeOnEscape: true,
    closeOnOverlayClick: true,
  });

  if (!user) return null;

  // 企業名の省略表示
  const getDisplayCompanyName = (company: string) => {
    if (!company) return '会社名未設定';
    if (isMobile && company.length > 12) {
      return company.substring(0, 12) + '...';
    }
    if (isTablet && company.length > 20) {
      return company.substring(0, 20) + '...';
    }
    return company;
  };

  // ユーザー名の省略表示
  const getDisplayUserName = (name: string) => {
    if (!name) return 'ユーザー名未設定';
    if (isMobile && name.length > 8) {
      return name.substring(0, 8) + '...';
    }
    return name;
  };

  // ページコンテキストを取得（ヘルプ用）
  const getPageContext = () => {
    const path = location.pathname;
    if (path.includes('/cases')) return 'cases';
    if (path.includes('/schedule')) return 'schedule';
    if (path.includes('/master')) return 'master';
    if (path.includes('/reports')) return 'reports';
    if (path.includes('/dashboard')) return 'dashboard';
    return undefined;
  };

  return (
    <header className='bg-white dark:bg-gray-800 shadow-sm p-4 flex justify-between items-center z-30'>
      {/* 左側: ハンバーガーメニュー + タイトル */}
      <div className='flex items-center space-x-4'>
        {/* モバイル・タブレット時のハンバーガーボタン */}
        {(isMobile || isTablet) && (
          <button
            onClick={onMenuClick}
            className={iconButtonStyles('ghost', '44')}
            aria-label='メニューを開く'
          >
            <Bars3Icon className='h-6 w-6 text-gray-600 dark:text-gray-300' />
          </button>
        )}

        {/* ロゴとタイトル・企業名 */}
        <div className='flex items-center space-x-3'>
          {/* ラクハイロゴ */}
          <img src='/logo.webp' alt='ラクハイ' className='h-8 w-8 sm:h-10 sm:w-10' />

          <div>
            {/* デスクトップでのみタイトル表示 */}
            {!isMobile && (
              <div className='flex items-center space-x-2'>
                <h1 className='text-xl font-semibold text-gray-800 dark:text-white'>ラクハイ</h1>
                {/* 環境バッジ（デモ・ステージング等） */}
                <HeaderEnvironmentBadge />
              </div>
            )}
            {/* モバイル時の環境バッジ（タイトルの代わり） */}
            {isMobile && <HeaderEnvironmentBadge />}
            {user.role !== Role.SUPER_ADMIN && (
              <div className='flex items-center text-sm text-gray-500 dark:text-gray-400 mt-1'>
                <BuildingOffice2Icon className='h-4 w-4 mr-2' />
                <span title={user.company}>{getDisplayCompanyName(user.company)}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 右側: ヘルプボタン + ユーザー情報とアクション */}
      <div className='flex items-center space-x-2'>
        {/* ヘルプボタン */}
        <HelpButton onClick={() => setIsHelpOpen(true)} size={isMobile ? 'sm' : 'md'} />
        {/* デスクトップ: 従来通りの表示 */}
        {!isMobile && !isTablet && (
          <>
            <div className='text-right'>
              <p className='font-semibold text-gray-700 dark:text-gray-200'>{user.name}</p>
              <p className='text-sm text-gray-500 dark:text-gray-400'>{getRoleLabel(user.role)}</p>
            </div>
            <Link
              to='/profile'
              className={iconButtonStyles('ghost', '44', 'rounded-full')}
              aria-label='プロフィール'
            >
              <UserIcon className='h-6 w-6 text-gray-600 dark:text-gray-300' />
            </Link>
            <Button onClick={handleLogout} variant='danger' size='md' className='space-x-2'>
              <ArrowRightOnRectangleIcon className='h-5 w-5' />
              <span>ログアウト</span>
            </Button>
          </>
        )}

        {/* モバイル・タブレット: ドロップダウンメニュー */}
        {(isMobile || isTablet) && (
          <div className='relative'>
            <button
              ref={dropdownButtonRef}
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className={cn(iconButtonStyles('ghost', '44'), 'space-x-2 px-3')}
              aria-label='ユーザーメニュー'
              aria-expanded={isDropdownOpen}
              aria-haspopup='true'
            >
              <UserIcon className='h-6 w-6 text-gray-600 dark:text-gray-300' />
              <div className='text-left'>
                <p className='font-semibold text-gray-700 dark:text-gray-200 text-sm'>
                  {getDisplayUserName(user.name)}
                </p>
                {!isMobile && (
                  <p className='text-xs text-gray-500 dark:text-gray-400'>
                    {getRoleLabel(user.role)}
                  </p>
                )}
              </div>
              <ChevronDownIcon className='h-4 w-4 text-gray-400' />
            </button>

            {/* ドロップダウンメニュー */}
            {isDropdownOpen && (
              <>
                {/* オーバーレイ */}
                <div
                  className={cn(overlayStyles(), 'z-40')}
                  onClick={handleOverlayClick}
                  aria-hidden='true'
                />

                {/* メニュー */}
                <div
                  ref={dropdownRef}
                  className='absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50'
                  role='menu'
                  aria-labelledby='user-menu-button'
                >
                  <div className='p-3 border-b border-gray-200 dark:border-gray-700'>
                    <p className='font-semibold text-gray-700 dark:text-gray-200'>{user.name}</p>
                    <p className='text-sm text-gray-500 dark:text-gray-400'>
                      {getRoleLabel(user.role)}
                    </p>
                  </div>
                  <div className='py-2'>
                    <button
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleProfileClick();
                      }}
                      className='w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition flex items-center space-x-2 min-h-[44px] focus:outline-none focus:bg-gray-100 dark:focus:bg-gray-700 touch-target'
                      role='menuitem'
                    >
                      <UserIcon className='h-4 w-4' />
                      <span>プロフィール</span>
                    </button>
                    <button
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleLogout();
                      }}
                      className='w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition flex items-center space-x-2 min-h-[44px] focus:outline-none focus:bg-red-50 dark:focus:bg-red-900/20 touch-target'
                      role='menuitem'
                    >
                      <ArrowRightOnRectangleIcon className='h-4 w-4' />
                      <span>ログアウト</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* ヘルプモーダル */}
      <HelpModal
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        pageContext={getPageContext()}
      />
    </header>
  );
};

export default Header;
